<template>
  <div @click="selectedItem = null">
    <breadcrumbs home="Assembly">
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left max-width="500">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <production-assembly-preferences @update="preferences=$event"/>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container fluid class="pt-6 px-6 purtrak-scrollable">
          <v-row dense>
            <v-col cols="6" sm="4" md="3">
              <date-picker v-model="assembly.date" @change="refreshLot()"/>
            </v-col>
            <v-col cols="6" sm="3" lg="2" v-if="preferences.assemblyDuration">
              <duration-picker label="Duration" v-model="assembly.duration" dense/>
            </v-col>
            <v-col cols="4" sm="2" v-if="preferences.assemblyIncomplete">
              <v-checkbox label="Completed" v-model="assembly.completed" dense class="mt-1"  @change="validate()"/>
            </v-col>
            <v-col cols="4" sm="3" lg="2" :offset-md="3+(preferences.assemblyDuration?0:2)+(preferences.assemblyIncomplete?0:2)">
              <v-text-field label="#" v-model="assembly.number" :rules="[fieldRules.required,fieldRules.maxLength(20)]" outlined dense :counter="20"/>
            </v-col>
          </v-row>
          <v-row dense v-if="sites.length>1" >
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete outlined dense label="Site" v-model="assembly.siteId" :items="sites" item-value="id" item-text="name"/>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete v-model="assembly.itemId" outlined dense label="Item to assemble" :rules="[requiredAlways]"
                              @change="selectItem"
                              :items="assembledItems" item-text="name" item-value="id"
                              no-data-text="No assembled items found"/>
            </v-col>
            <v-col cols="6" sm="2">
              <decimal-number-input label="Quantity" v-model="assembly.quantity" :precision="item.precision"  prefix="" :suffix="item.trackUnits"/>
            </v-col>
            <v-col cols="6" sm="2">
              <v-text-field v-model="assembly.lot"
                            label="Item lot" outlined dense
                            :rules="[required,fieldRules.maxLength(20)]" :counter="20"/>
            </v-col>
          </v-row>
          <v-row v-if="item.description" class="mb-4">
            <v-col cols="12" lg="8" class="primary--text">
              <em>{{item.description}}</em>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" lg="8">
              <outlined-block title="Bill of Materials" v-model="assembly.bom" :rules="[bomCheck]" >
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="assembly.bom" item-key="uniqueId" no-data-text="No items specified"
                              :headers="[{text:'',value:'add',width:'15%',align:'center',cellClass:'pa-0',divider:true},
                                        {text:'Item',value:'item',width:'35%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                                        {text:'Quantity',value:'quantity',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'right'},
                                        {text:'Lot',value:'lot',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'}]"  class="no-sort"
                                :item-class="item => item.uniqueId=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                                @click.native.stop=""
                                @click:row="item => {selectedItem = item.uniqueId; $nextTick(() => this.$refs.form.validate())}"
                              ref="bomTableBody">
                  <template v-slot:item.add="{item,index}">
                    <v-icon class="handle mt-n2" :disabled="!edit">mdi-drag</v-icon>
                    <v-btn class="mt-n2" icon @click.stop="deleteItem(index)" x-small :disabled="!edit"><v-icon small>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.item="{item}">
                    <template v-if="selectedItem === item.uniqueId">
                      <multi-level-select v-if="categories.length" outlined label="Item"
                                    :menuData="eligibleItems" :second.sync="item.id" @change="changeBOMItem(item, false)"/>
                      <v-autocomplete v-else dense label="Item" v-model="item.id" outlined :items="items" item-value="id" item-text="name" class="text-body-2" @change="changeBOMItem(item, false)"/>
                    </template>
                    <div v-else class="ml-1" :class="{'error--text':!item.id}">
                      {{bomItem(item.id).name}}
                    </div>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <decimal-number-input v-if="selectedItem === item.uniqueId" label="Quantity" v-model="item.quantity" prefix=""
                          :suffix="bomItem(item.id).trackUnits" :precision="bomItem(item.id).precision" class="text-body-2" @change="validate()"/>
                    <div v-else class="mr-1" :class="{'error--text':item.quantity < 0}">
                      {{item.quantity | number(bomItem(item.id).precision)}} {{bomItem(item.id).trackUnits}}
                    </div>
                  </template>
                  <template v-slot:item.lot="{item}">
                    <template v-if="bomItem(item.id).lotTracking">
                      <v-combobox v-if="selectedItem === item.uniqueId" dense outlined label="Lot" v-model="item.lot" :rules="[required,fieldRules.maxLength(20)]" :items="currentLots(item.id)"
                                  class="text-body-2" no-data-text="No inventory"/>
                      <div v-else class="ml-1" :class="{'error--text':required(item.lot) !== true}">
                        {{item.lot || 'Required'}}
                      </div>
                    </template>
                    <div v-else>n/a</div>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedInputItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn :disabled="!edit || items.length===0" fab color="primary" x-small @click.stop="addItem"><v-icon small>mdi-plus</v-icon></v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-text-field label="Comments" v-model="assembly.comments" outlined dense counter="100" :rules="[fieldRules.maxLength(100)]"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
      <template v-if="assemblyId && company.permissions.includes('production.write')">
        <v-switch :disabled="dirty" v-model="edit" label="Edit" hide-details class="mt-0"/>
      </template>
      <v-spacer/>
      <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
      <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>
</template>

<script>
import {format} from 'date-fns';
import sortableJS from 'sortablejs';
import confirmLeave from '@/mixins/confirmLeave'
import ProductionAssemblyPreferences from '@/views/production/assembly/ProductionAssemblyPreferences'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'ProductionAssemblyEdit',
  components: {ProductionAssemblyPreferences},
  mixins: [confirmLeave,createHierarchyList],
  props: {
    assemblyId: {type:String, required:false},
    itemId: {type:String, required:false},
    quantity: {type:Number, required:false, default:0},
    activityId: {type:String, required:false}
  },
  data () {
    return {
      edit: false,
      assembly: {date:format(new Date(), 'yyyy-MM-dd HH:mm:ss'),number:'',itemId:'',
                 siteId:this.$store.getters.sites(true)[0].id,
                 completed:true,duration:0,comments:'',quantity:this.quantity,lot:'',bom:[],
                 activityId:this.activityId},
      items: [],
      categories: [],
      preferences: {},

      dirty: false,
      saving: false,
      loading: false,
      selectedItem: null,
      valid: true,
      requiredAlways: v => !!v || 'Required',
      required: v => !this.assembly.completed || !!v || 'Required',
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.assemblyId);
    },
    assembledItems() {
      return this.items.filter(item => item.assembled);
    },
    item() {
      return this.items.find(item => item.id === this.assembly.itemId) || {}
    },
    eligibleItems() {
      const categories = this.createHierarchyList(this.categories);
      for (const category of categories) {
        const sub = this.items.filter(item => item.category?.id === category.id);
        if (sub.length)
          category.menu = sub;
        else
          category.disabled = true;
      }
//      const items = [...categories.filter(category => !category.disabled)];
      const items = categories;
      const leftOver = this.items.filter(item => !item.category?.id);
      if (leftOver.length)
        items.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
      return items;
    }
  },
  async created() {
    this.loading = true;
    this.$lazyListLoad(this.categories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]);
    await this.$lazyListLoad(this.items, 'items2', 'id,name,trackUnits,lotTracking,assembled,precision,category{id}', 100, !this.assemblyId ? [{field:'active', comparator:'eq', value:'true'}] : []);
    const results = await this.$apiCall(`query ProductionAssemblyEdit($edit:Boolean!,$id:ID!) {
                                           assembly(id:$id) @include(if:$edit) {
                                             id,date,number,created,siteId,itemId,completed,duration,comments,quantity,lot,precision
                                             bom{id,quantity,lot}
                                           }
                                            assemblingPreferences {
                                              assemblyDuration,assemblyIncomplete
                                            }
                                         }`,
                                         {edit:this.assemblyId!==undefined,
                                          id:this.assemblyId || ''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.preferences = results.data.assemblingPreferences;

    if (this.assemblyId) {
      let uniqueId = Date.now();
      results.data.assembly.bom.forEach(item => item.uniqueId = uniqueId++);
      this.assembly = results.data.assembly;
      if (!this.assembly.completed)
        this.assembly.duration = this.$roundNumber(Date.now()/60000 - this.assembly.created, 0);
      await this.selectItem();
      if (!this.assembly.completed)
        this.edit = true;
    }
    else if (this.items.length) {
      const results = await this.$apiCall(`query ProductionAssemblyEdit {
                                              assemblyNextNumber
                                            }`);
      if (!results.error)
        this.assembly.number = results.data.assemblyNextNumber;
      this.assembly.itemId = this.itemId || '';
      this.selectItem();
      this.edit = true;
    }

    await this.refreshLot();
    this.$nextTick(() => this.dirty = false);
    this.validate();
  },
  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.bomTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.assembly.bom.splice(oldIndex, 1)[0];
        _self.assembly.bom.splice(newIndex, 0, rowSelected);
      }
    })
  },
  beforeDestroy() {
    this.sortable.destroy();
  },
  watch: {
    assembly: {
      handler() {
        this.dirty = true;
      },
      immediate: true,
      deep: true
    },
    'assembly.quantity'() {
      this.bom();
    }
  },

  methods: {
    validate() {
      this.$nextTick(() => {
        this.$refs.form.validate();
      });
    },
    bomCheck() {
      if (!this.edit || !this.assembly.completed)
        return true;
      if (!this.item || !this.item.bom)
        return false;

      // Must meet minimum requirements on item BOM
      for (const item of this.item.bom) {
        if (item.optional)
          continue;
        let quantity = item.quantity * this.assembly.quantity;
        this.assembly.bom.forEach(bomItem => {
          if (bomItem.id === item.id)
            quantity -= bomItem.quantity;
        });
        if (quantity > 0) {
          const i = this.bomItem(item.id);
          return `A minimum ${item.quantity * this.assembly.quantity} ${i.trackUnits} of ${i.name} required`;
        }
      }
      return true;
    },
    addItem() {
      const bomItem = {id:'',quantity:0,lot:null,uniqueId:Date.now()};
      this.changeBOMItem(bomItem, false);
      this.selectedItem = bomItem.uniqueId;
      this.assembly.bom.push(bomItem);
      this.validate();
    },
    deleteItem(index) {
      this.assembly.bom.splice(index, 1);
    },
    async changeBOMItem(bomItem, forceRefresh) {
      const item = this.bomItem(bomItem.id);
      if (!item.id)
        return;
      if (forceRefresh || (item.lotTracking && !item.sites)) {
        const results = await this.$apiCall(`query ProductionAssemblyEdit($id:ID!, $date:DateTime!) {
                                                  item(id:$id) {
                                                    sites(asof:$date) {siteId,lot}
                                                  }
                                                }`,
                                            {id:item.id,date:this.assembly.date});
        if (!results.error)
          this.$set(item, 'sites', results.data.item.sites);
      }
      this.validate();
    },
    currentLots(id) {
      const item = this.bomItem(id);
      return item && item.sites ?
                                item.sites.filter(site => site.siteId === this.assembly.siteId).map(site => site.lot)
                                : [];
    },

    async selectItem() {
      if (this.assembly.itemId && !this.item.bom) {
        const results = await this.$apiCall(`query ProductionAssemblyEdit($id:ID!) {
                                              item(id:$id) {
                                                description,bom{id,quantity,optional}
                                              }
                                            }`,
                                            {id:this.item.id});
        if (!results.error) {
          this.item.description = results.data.item.description;
          this.item.bom = results.data.item.bom;
        }
      }
      this.bom();
    },
    bomItem(itemId) {
      return this.items.find(item => item.id === itemId) ?? {trackUnits:'', precision:0, name:'MISSING', lotTracking:false};
    },
    bom() {
      if (!this.item || !this.item.bom || !this.edit)
        return;
      let uniqueId = Date.now();
      this.assembly.bom = this.item.bom.map(item => {
        const lot = this.assembly.bom.find(i => i.id === item.id)?.lot;
        return {id:item.id,quantity:item.quantity*this.assembly.quantity,lot,uniqueId:uniqueId++};
      });
      for (const bomItem of this.assembly.bom)
        this.changeBOMItem(bomItem, false);
      this.validate();
    },
    async refreshLot() {
      if (this.assemblyId)    // Don't do for editing existing assembly
        return;
      this.loading = true;
      const results = await this.$apiCall(`query ProductionAssemblyEdit($asof:DateTime!) {
                                            lotUnique(prefix:"I",date:$asof)
                                          }`,
                                          {asof:this.assembly.date});
      if (!results.error)
        this.assembly.lot = results.data.lotUnique;

      for (const bomItem of this.assembly.bom)
        await this.changeBOMItem(bomItem, true);

      this.loading = false;
    },
    cancel() {
      this.$router.back();
    },
    async save() {
      const incomplete = this.assembly.bom.some(item => !item.id ||
                                                        item.quantity < 0 ||
                                                        (this.bomItem(item.id).lotTracking && this.required(item.lot) !== true));
      if (!this.valid || incomplete) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {created,precision, ...input} = this.assembly;
      input.bom = this.assembly.bom.map(item => {
        // eslint-disable-next-line no-unused-vars
        const {uniqueId, ...i} = item;
        return i;
      });
      const results = await this.$apiCall(`mutation ProductionAssemblyEdit($input: AssemblyInput!) {
                                             assemblyCreate(input:$input) {id}
                                           }`,
                                          {input})

      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.assembly.id?'Updated':'Created'} assembly for ${this.item.name}`});
        if (this.activityId)
          this.$store.dispatch('checkTasks', true);
      }
    }
  }
}
</script>